import type {
  CaseTypesFilterParams,
  CreateCaseTypeDto,
  PaginatedCaseTypesResponseDto,
  UpdateCaseTypeDto,
} from '@peritasai/types';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CaseTypeEntity } from './case-type.entity';
import { caseTypesRepository } from './repository';

export const caseTypesKeys = {
  all: ['case-types'] as const,
  lists: () => [...caseTypesKeys.all, 'list'] as const,
  list: (filters: CaseTypesFilterParams) =>
    [...caseTypesKeys.lists(), { filters }] as const,
} as const;

export function useCaseTypes(
  filters: CaseTypesFilterParams = {}
): UseQueryResult<PaginatedCaseTypesResponseDto & { caseTypes: CaseTypeEntity[] }> {
  return useQuery({
    queryKey: caseTypesKeys.list(filters),
    queryFn: () => caseTypesRepository.getCaseTypesPaginated(filters),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useCreateCaseType(): UseMutationResult<CaseTypeEntity, Error, CreateCaseTypeDto> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateCaseTypeDto) => caseTypesRepository.createCaseType(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: caseTypesKeys.lists() });
    },
  });
}

export function useUpdateCaseType(): UseMutationResult<
  CaseTypeEntity,
  Error,
  { id: string; dto: UpdateCaseTypeDto }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateCaseTypeDto }) =>
      caseTypesRepository.updateCaseType(id, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: caseTypesKeys.lists() });
    },
  });
}
